import { IProduct } from '@/api/products/queries/get-product-query'
import { IApi } from '@/setup/api-client'
import { useInfiniteApiQuery } from '@/utils/api/api-infinite-query'
import { useApiQuery } from '@/utils/api/api-query'
import { IApiError, IApiQueryParams } from '@/utils/api/api-types'
import { useApiToolbox } from '@/utils/api/use-api-toolbox'
import { omit } from '@/utils/functions/omit'
import { keepPreviousData, queryOptions, UseQueryOptions } from '@tanstack/react-query'

export const PRODUCT_QUERY_BASE_KEY = ['products']
export const PRODUCTS_PAGE_SIZE = 50

// Meta may be undefined when the frontend deploys before the backend
// (old backend returns a bare array without meta).
export type IGetProductsMeta = { page?: number; limit?: number; search?: string; total?: number } | undefined

export interface IProductsQueryParams {
    organizationId: string
    brandSlug: string
    db?: 'primary' | 'replica'
    page?: number
    limit?: number
    search?: string
}

export class GetProductsQuery {
    // Tagging the queryKey with IProduct[] here propagates through TanStack's
    // DataTag so setQueryData(key, ...) and useQueryClient().getQueryData(key)
    // are type-checked against the stored shape. Without this they default to
    // unknown and cache/queryFn drift is silent.
    static getKey(params: IProductsQueryParams) {
        return queryOptions<IProduct[], IApiError>({
            queryKey: [
                ...PRODUCT_QUERY_BASE_KEY,
                params.organizationId,
                params.brandSlug,
                'products',
                { db: params.db, page: params.page, limit: params.limit, search: params.search },
            ],
        }).queryKey
    }

    static async call(queryClient: IApi, params: IProductsQueryParams) {
        const queryParams: IApiQueryParams = {}
        if (params.db) queryParams.db = params.db
        if (params.page !== undefined) queryParams.page = params.page
        if (params.limit !== undefined) queryParams.limit = params.limit
        if (params.search?.trim()) queryParams.search = params.search.trim()

        return queryClient.get<IProduct[], IGetProductsMeta>(
            `/organization/${params.organizationId}/brands/${params.brandSlug}/products`,
            queryParams
        )
    }
}

export function useGetProductsQuery(
    params?: { organizationId?: string; brandSlug?: string; db?: 'primary' | 'replica' },
    options?: Omit<UseQueryOptions<IProduct[], IApiError, IProduct[]>, 'queryKey' | 'queryFn'>
) {
    const { api, apiParams } = useApiToolbox()

    const organizationId = params?.organizationId ?? apiParams.organizationId
    const brandSlug = params?.brandSlug ?? apiParams.brandSlug

    return useApiQuery({
        queryKey: GetProductsQuery.getKey({ organizationId: organizationId ?? null, brandSlug, db: params?.db }),
        queryFn: () =>
            GetProductsQuery.call(api, { organizationId: organizationId ?? '', brandSlug, db: params?.db }).then(
                (res) => res?.data
            ),
        enabled: !!organizationId && !!brandSlug && (options?.enabled ?? true),
        ...omit(options || {}, 'enabled'),
    })
}

interface IUseGetPaginatedProductsQueryOptions {
    page: number
    limit?: number
    search?: string
    enabled?: boolean
}

/**
 * Single-page, searchable products query for classic page-based pagination.
 * Backed by the GET /products endpoint with page/limit/search query params.
 */
export function useGetPaginatedProductsQuery(options: IUseGetPaginatedProductsQueryOptions) {
    const { api, apiParams } = useApiToolbox()
    const organizationId = apiParams.organizationId ?? ''
    const brandSlug = apiParams.brandSlug
    const limit = options.limit ?? PRODUCTS_PAGE_SIZE
    const search = options.search

    // Returns the full response (data + meta) so callers can read meta.total for pagination.
    return useApiQuery({
        queryKey: GetProductsQuery.getKey({ organizationId, brandSlug, page: options.page, limit, search }),
        queryFn: () => GetProductsQuery.call(api, { organizationId, brandSlug, page: options.page, limit, search }),
        enabled: !!organizationId && !!brandSlug && (options.enabled ?? true),
        placeholderData: keepPreviousData,
    })
}

interface IUseGetInfiniteProductsQueryOptions {
    search?: string
    limit?: number
    enabled?: boolean
    brandSlug?: string
}

/**
 * Paginated, searchable products query. Backed by the GET /products endpoint with
 * page/limit/search query params. Use this for selectors / lists that should not
 * load every product at once.
 */
export function useGetInfiniteProductsQuery(options?: IUseGetInfiniteProductsQueryOptions) {
    const { api, apiParams } = useApiToolbox()
    const organizationId = apiParams.organizationId ?? ''
    const brandSlug = options?.brandSlug ?? apiParams.brandSlug
    const limit = options?.limit ?? PRODUCTS_PAGE_SIZE
    const search = options?.search

    return useInfiniteApiQuery({
        queryKey: [...PRODUCT_QUERY_BASE_KEY, 'infinite', organizationId, brandSlug, { limit, search }],
        queryFn: ({ pageParam = 1 }) =>
            GetProductsQuery.call(api, {
                organizationId,
                brandSlug,
                page: pageParam as number,
                limit,
                search,
            }),
        initialPageParam: 1,
        getNextPageParam: (lastPage) => {
            if (!lastPage?.data || lastPage.data.length === 0) return null
            if (lastPage.data.length < limit) return null
            const lastPageNumber = lastPage.meta?.page ?? 1
            return lastPageNumber + 1
        },
        enabled: !!organizationId && !!brandSlug && (options?.enabled ?? true),
        structuralSharing: false,
        placeholderData: keepPreviousData,
    })
}
